window.ontech = window.ontech || {};

// See documentation at ontech.state.readme.md

window.ontech.state = (function (actions) {
    'use strict'

    // public functions
    let doAction,
        getState,           // If no params, return entire state, if pass in key, return value for key
        getStateJson,       // Return state as JSON string
        init,               // Read state from localStorage, or initialize default state
        isInitialized,
        resetState,         // Reset state to default
        logActivity;        // Appends an activity to the activity log

    try {
        // private variables
        const ITEM_KEY = 'ONTECH.DEVTOOLS.id';
        const SESSION_KEY = 'ONTECH.STATE.SESSION';
        const STATE_PREFIX = 'ONTECH.STATE.';

        let state,

        // private functions
            checkMissingKey,
            clearState,
            dirtyKeysQue = [],
            getLocalStorage,
            getStackTrace,
            hasStateKey,
            logState,
            logStateError,
            initLocalStorage,
            setLocalStorage,
            setState,
            validateClearState,
            validateDoAction,
            validateSetState;

        /**
         * generates a stack trace for use in the custom devtools extension
         * 
         * @private
         */
        getStackTrace = function () {
            let stack,
                stackFinal,
                urlBegin,
                urlQueryVBegin,
                urlQueryVEnd,
                urlQueryReferrerBegin,
                trimmedUrl;
          
            try {
              throw new Error('Ignore getStackTrace');
            } catch (error) {
              stack = error.stack || '';
            }
          
            stack = stack.split('\n').map(function (line) { return line.trim(); });
            stackFinal = stack.splice(stack[0] === 'Error' ? 2 : 1);

            // trim the url from the second entry in the stack
            urlBegin = stackFinal[1].indexOf('http');          
            if (urlBegin !== -1) {
                trimmedUrl = stackFinal[1].slice(urlBegin);
                // handle case of queryString v=                
                urlQueryVBegin = trimmedUrl.indexOf('?v=');
                urlQueryVEnd = trimmedUrl.indexOf(':', 6);                
                if (urlQueryVBegin !== -1) {
                    trimmedUrl = trimmedUrl.substring(0, urlQueryVBegin) + trimmedUrl.substring(urlQueryVEnd, trimmedUrl.indexOf(')'));
                } else if (trimmedUrl.indexOf(')')) {
                    trimmedUrl = trimmedUrl.slice(0, trimmedUrl.indexOf(')'));
                }

                // handle case of queryString referrer=
                urlQueryReferrerBegin = trimmedUrl.indexOf('?referrer=');
                if (urlQueryReferrerBegin !== -1) {
                    trimmedUrl = trimmedUrl.substring(0, urlQueryReferrerBegin);
                }

                return trimmedUrl;
            } else {
                return '-';
            }
        }

        /**
         * invokes the provided action function.
         * updates changed state in localStorage once the action has completed.
         * 
         * @public
         * @param {function} action Function containing set and/or clear calls.
         * @param {*|undefined} params Parameters to be passed to the invoked action (optional).
         */
        doAction = function (action, params) {
            try {
                if (!validateDoAction(action)) {
                    return undefined;
                } else {
                    let prevState, result, stackTrace;

                    prevState = getState();
                    // call the action
                    result = actions[action](params);
                    stackTrace = getStackTrace();
                    // log to the devtools extension
                    logState('do: ' + action, '', '', prevState, getState(), stackTrace);
                    // log to the activity log
                    logActivity(action);
                    // update localStorage
                    setLocalStorage();
                    return result;
                }
            } catch (err) {
                let errMsg = `doAction error in ontech.state - action: ${action}`;
                ontech.log.error(errMsg, err);
            }
        };   

       /**
         * validates action for doAction
         * 
         * @private
         * @param {string} action The provided action.
         * @returns {boolean} Returns true or false based on validity.
         */         
        validateDoAction = function (action) {
            try {
                // action is required
                if (isUndefinedOrNull(action)) {
                    logStateError('doAction', action, null, 'action required');
                    return false;
                }

                // actions object must exist
                if (isUndefinedOrNull(actions)) {
                    logStateError('doAction', action, null, 'actions object does not exist');
                    return false;
                }     
                
                // action must exist on the actions object
                if (!_.has(actions, action)) {
                    logStateError('doAction', action, null, 'action does not exist on actions object');
                    return false;
                }

                // action must be a function
                if (isFunction(actions[action])) {
                    return true;
                } else {
                    logStateError('doAction', action, null, 'action is not a function');
                    return false;
                }
            } catch (err) {
                let errMsg = `validateDoAction error in ontech.state - action: ${action}`;
                ontech.log.error(errMsg, err);
            }                 
        }        
        
        /**
         * state getter
         * 
         * @public
         * @param {string|undefined} key The state key or property in dot notation (optional).
         * @returns {*|undefined} The value of the state key or undefined if the key does not exist.
         */
        getState = function (key) {
            try {
                let clonedResponse;

                // initialize state if not yet initialized
                // handles race between document.ready functions
                if (!ontech.state.isInitialized()) {
                    ontech.state.init();
                }

                // if state is present and key is blank,
                // return the entire state object
                if (state && isEmpty(key)) {
                    // cloneDeep the state response
                    // to break reference to the state object
                    clonedResponse = _.cloneDeep(state);
                    logState('getState', key, clonedResponse);
                    return clonedResponse;
                }
                // if state is present and key is provided
                // and provided key exists on state
                else if (state && key && hasStateKey(key)) {
                    clonedResponse = _.cloneDeep(_.get(state, key));
                    logState('getState', key, clonedResponse);
                    return clonedResponse;
                } else {                  
                    logStateError('getState', key, null, 'key not found');
                    return undefined;
                }
            } catch (err) {
                let errMsg = `getState error in ontech.state - key: ${key}`;
                ontech.log.error(errMsg, err);
            }
        };

        /**
         * Get the state contents as a string
         */
        getStateJson = function () {
            return JSON.stringify(ontech.state.get());
        }

        /**
         * state setter
         * 
         * @private 
         * @param {string} key The state key or property in dot notation.
         * @param {*} value The value sent to update the state key.
         * @returns {*|undefined} The value of the newly set state key or undefined if the key doesn't exist.
         */
        setState = function (key, value) {
            try {
                let prevState, clonedValue;
                // initialize state if not yet initialized
                // handles race between document.ready functions
                if (!ontech.state.isInitialized()) {
                    ontech.state.init();
                }

                if (!validateSetState(key, value)) {
                    return undefined;
                } else {
                    // cloneDeep the incoming value
                    // to break any references 
                    // to other parts of the app                    
                    // set the state value        
                    let group = key.split('.')[0];
                    clonedValue = _.cloneDeep(value);
                    prevState = getState();
                    _.set(state, key, clonedValue);
                    // add the top level object for the key
                    // to the dirtyKeysQue
                    // for localStorage update
                    dirtyKeysQue.push(group);
                    // log the previous and next states    
                    logState('setState', key, clonedValue, prevState, state);
                    return state[key];
                }
            } catch (err) {
                let errMsg = `setState error in ontech.state - key: ${key}`;
                ontech.log.error(errMsg, err);
            }    
        }

        /**
         * appends an item to the activity log
         * 
         * @public
         * @param activity {string} The activity to be logged.
         * @returns {undefined} 
         */        
        logActivity = function (activity) {
            try {
                let activityLog;
                // change this key in constants to enable state activity logging
                if (!State.WRITE_ACTIVITY_LOG) {
                    return;
                }

                // activity is required
                if (isUndefinedOrNull(activity)) {
                    logStateError('logActivity', null, null, 'activity required');
                    return;
                }

                // get the current activity log (log.activityLog)
                activityLog = getState(State.LOG_ACTIVITY);

                // activityLog is required
                if (isUndefinedOrNull(activityLog)) {
                    logStateError('logActivity', null, null, 'log.activityLog missing from state. Reset State, refresh and try again.');
                    return;
                }

                activityLog.push(activity);
                setState(State.LOG_ACTIVITY, activityLog);
            } catch (err) {
                let errMsg = `logActivity error in ontech.state - activity: ${activity}`;
                ontech.log.error(errMsg, err);
            }
        }

        /**
         * validates parameters for setState
         * 
         * @private
         * @param {string} key The state key or property in dot notation.
         * @param {*} value The value sent to update the state key.
         * @returns {boolean} Returns true or false based on validity of parameters.
         */
        validateSetState = function (key, value) {
            try {
                // key is required
                if (isUndefinedOrNull(key)) {
                    logStateError('setState', key, value, 'key required');
                    return false;
                }
            
                // value is required and may not be undefined
                if (value === undefined) {
                    logStateError('setState', key, value, 'value is required. value may not be undefined (use null).');
                    return false;
                }

                // state is required
                if (isUndefinedOrNull(state)) {
                    logStateError('setState', key, value, 'state is required');
                    return false;
                }

                // key must already exist on state
                // setter cannot add new keys to state
                if (hasStateKey(key)) {
                    return true;
                } else {
                    logStateError('setState', key, value, 'key does not exist on state');
                    return false;
                }
            } catch (err) {
                let errMsg = `validateSetState error in ontech.state - key: ${key}`;
                ontech.log.error(errMsg, err);
            }
        }        

        /**
         * sets a state key to its default value
         * migration note: use this in place of cookie remove.
         * 
         * @private 
         * @param {string} key The state key or property in dot notation.
         * @returns {*|undefined} The value of the newly set state key or undefined if the key doesn't exist.
         */        
        clearState = function (key) {
            try {
                let keyDefaultState;
                // initialize state if not yet initialized
                // handles race between document.ready functions
                if (!ontech.state.isInitialized()) {
                    ontech.state.init();
                }

                if (!validateClearState(key)) {
                    return undefined;
                } else {
                    // get the default value for the provided key
                    keyDefaultState = _.cloneDeep(_.get(window.ontech.defaultState, key));

                    // set the state key to the default value
                    return setState(key, keyDefaultState);
                }
            } catch (err) {
                let errMsg = `clearState error in ontech.state - key: ${key}`;
                ontech.log.error(errMsg, err);
            }
        }

       /**
         * validates key for clearState
         * 
         * @private
         * @param {string} key The state key or property in dot notation.
         * @returns {boolean} Returns true or false based on validity of key.
         */        
        validateClearState = function (key) {
            try {
                // key is required
                if (isUndefinedOrNull(key)) {
                    logStateError('clearState', key, null, 'key required');
                    return false;
                }

                // key must already exist on defaultState
                // setter cannot add new keys to state
                if (_.has(window.ontech.state.defaultState, key)) {
                    return true;
                } else {
                    logStateError('clearState', key, null, 'key does not exist on defaultState');
                    return false;
                }
            } catch (err) {
                let errMsg = `validateClearState error in ontech.state - key: ${key}`;
                ontech.log.error(errMsg, err);
            }                
        }         

        /**
         * validates that a provided key exists on the state object
         * for missing keys, calls updateState to check for a newly added key
         * (handles the edge case of a key added by dev during a user session)
         * 
         * @private
         * @param {string} key The key or property in dot notation.
         * @returns {boolean} Returns true or false based on existence of key on state. 
         */
        hasStateKey = function (key) {
            try {
                if (isUndefinedOrNull(key)) {
                    logStateError('hasStateKey', key, null, 'key required');
                    return false;
                } else {
                    if (_.has(state, key)) {
                        return true
                    } else {
                        return checkMissingKey(key);
                    }
                }
            } catch (err) {
                let errMsg = `hasStateKey error in ontech.state - key: ${key}`;
                ontech.log.error(errMsg, err);
            }
        }

        /**
         * called by hasStateKey when a key is missing from state
         * if key is present on defaultState, adds the new key to state and updates localStorage
         * (handles the case of a key added by dev during a user session)
         * 
         * @private
         * @param {string} key The key or property in dot notation.
         * @returns {boolean} Returns true or false based on existence of new key on defaultState. 
         */        
        checkMissingKey = function (key) {
            try {
                if (_.has(window.ontech.defaultState, key)) {
                    // add the new key to state
                    _.merge(state, window.ontech.defaultState[key]);
                    // update localStorage with the new key
                    setLocalStorage(key);
                    return true;
                } else {
                    return false;
                }
            } catch (err) {
                let errMsg = `checkMissingKey error in ontech.state - key: ${key}`;
                ontech.log.error(errMsg, err);
            }
        }

        /**
         * sets the state object (or object key) to its corresponding localStorage value
         * 
         * @private
         * @param {string} key The key or property in dot notation.
         * @returns {*} Returns the state key's value. 
         */        
        getLocalStorage = function (key) {
            // if key is empty, iterate through state object
            // get each branch of properties as 'ONTECH.STATE.{key}'
            try {
                let localItem, prevState;
                prevState = getState();

                if (state && isEmpty(key)) {
                    for (const stateKey in state) {
                        if (stateKey) {
                            localItem = ontech.storage.getItem(`${STATE_PREFIX}${stateKey}`);
                            state[stateKey] = localItem;
                        }
                    }
                    logState('getLocalStorage', key, '', prevState, state);  
                    return state;
                }
                // if key is provided
                else if (state && key) {
                    // get top-level key for object
                    let topLevelKey = key.split('.')[0];
                    // top-level key must exist on state object
                    if (hasStateKey(topLevelKey)) {
                        localItem = ontech.storage.getItem(`${STATE_PREFIX}${key}`);
                        state[topLevelKey] = localItem;
                    }
                    logState('getLocalStorage', topLevelKey, '', prevState, state[topLevelKey]);                     
                    return state[topLevelKey];
                } else {
                    logStateError('getLocalStorage', null, null, 'key does not exist on state');  
                }                            
            } catch (err) {
                let errMsg = `setState error in ontech.state - key ${key}`;
                ontech.log.error(errMsg, err);
            }
        };

        /**
         * sets the localStorage state value to it's corresponding value on state object
         * 
         * @private
         * @param {string|undefined} key The state key or property in dot notation (optional).
         * @returns {object} Returns the localStorage item's value. 
         */    
        setLocalStorage = function (key) {
            try {
                // if key is provided, updates the top level key for the key parameter.
                if (key) {
                    // get top-level key for object
                    let topLevelKey = key.split('.')[0];
                    // key must exist on state
                    if (hasStateKey(topLevelKey)) {
                        let itemName = `${STATE_PREFIX}${topLevelKey}`;
                        ontech.storage.setItem(itemName, state[topLevelKey]);
                        logState('setLocalStorage', topLevelKey, state[topLevelKey], '', state);                        
                        ontech.storage.getItem(itemName);
                    } else {
                        logStateError('setLocalStorage', key, null, 'key does not exist on state');
                    }
                // if key is empty, updates top level items from the dirtyKeysQue.
                } else {
                    // de-dupe the dirtKeysQue
                    // iterate through dirtyKeysQueUnique
                    // and update each localStorage item
                    let dirtyKeysQueUnique = _.uniq(dirtyKeysQue);
                    if (dirtyKeysQueUnique && dirtyKeysQueUnique.length > 0) {
                        for (const dirtyKey of dirtyKeysQueUnique) {
                            if (hasStateKey(dirtyKey)) {
                                let itemName = `${STATE_PREFIX}${dirtyKey}`;
                                ontech.storage.setItem(itemName, state[dirtyKey]);
                                logState('setLocalStorage', itemName, JSON.stringify(state[dirtyKey]), '', state);
                            } else {
                                logStateError('setLocalStorage', null, null, 'key does not exist on state');  
                            }
                        }
                    } else {
                        logStateError('setLocalStorage', null, null, 'no keys provided');
                    }
                }
            } catch (err) {
                let errMsg = `setLocalStorage error in ontech.state - key: ${key}`;
                ontech.log.error(errMsg, err);
            }                
        }; 
        
        /**
         * initializes localStorage state
         * used if state is run for the first time
         * 
         * @private
         */    
        initLocalStorage = function () {
            try {
                // set the localStorage with the defaultState
                for (let key in window.ontech.state.defaultState) {
                    if (key) {
                        setLocalStorage(key);
                    }
                }              
            } catch (err) {
                let errMsg = `resetLocalStorage error in ontech.state`;
                ontech.log.error(errMsg, err);
            }                
        };        

        /**
         * creates a log of the state event
         * 
         * @private
         * @param {string} action name of the action invoked.
         * @param {string|undefined} key The key or property in dot notation (optional for getters).
         * @param {*|undefined} value The value sent to a setter (optional, setters only).
         * @param {Object} prevState The previous state before any changes were made (setters and getters).
         * @param {Object} nextState The next state after any changes were made (setters only).
         * @param stack Optional.
         */
        logState = function (action, key, value, prevState, nextState, stack = '') {
            try {
                let extensionId;
                if (ontech.storage.getItem(ITEM_KEY) !== null) {
                    extensionId = ontech.storage.getItem(ITEM_KEY);
                }
                
                if (action.startsWith('do')) {
                    if (window.chrome && window.chrome.runtime && extensionId) {
                        chrome.runtime.sendMessage(extensionId, { "action": action, "key": key, "value": value, "prevState": JSON.stringify(prevState), "currentState": JSON.stringify(nextState), "stack": stack });
                    }
                }
            } catch (err) {
                let errMsg = `logState error in ontech.state`;
                ontech.log.error(errMsg, err);
            }
        };

        /**
         * creates a log of the state error
         * 
         * @private
         * @param {string} action name of the action invoked.
         * @param {string|undefined} key The state key or property in dot notation (optional for getters).
         * @param {*} value The value sent to a setter (optional, setters only).
         * @param {string} error The error description.
         */
        logStateError = function (action, key, value, error) {
            try {
                let extensionId = 'mnpopdkpigdcjegjblccgonfanhbandc';
                if (ontech.storage.getItem(ITEM_KEY)) {
                    extensionId = ontech.storage.getItem(ITEM_KEY);
                }

                let stateError = `STATE ERROR: action: ${action}, key: ${key}, value: ${JSON.stringify(value)}, error: ${error}`;
                ontech.log.error(stateError);
                if (window.chrome && window.chrome.runtime) {
                    chrome.runtime.sendMessage(extensionId, { "action": action, "key": key, "value": value, "error": error });
                }
            } catch (err) {
                let errMsg = 'logState error in ontech.state';
                ontech.log.error(errMsg, err);
            }
        };     

        /**
         * initialize ontech.state module
         * set state to corresponding values in localStorage
         * and define any options (un-implemented)
         * 
         * @private
         * @returns {undefined} Returns undefined
         */
        init = function () {
            try {
                // set the initial state with defaultState
                state = _.cloneDeep(window.ontech.state.defaultState);

                // check for presence of 
                // ONTECH.STATE.SESSION cookie
                // if item not present, reset localStorage state
                // Note: cookie is set to expire after browser session
                if (!ontech.storage.getItem(SESSION_KEY)) {
                    initLocalStorage();
                    ontech.storage.setItem(SESSION_KEY, Date.now());
                } else {
                    // set state with the existing localStorage state
                    for (let key in window.ontech.state.defaultState) {
                        if (key) {
                            getLocalStorage(key);
                        }
                    }
                }
            } catch (err) {
                let errMsg = 'init error in ontech.state';
                ontech.log.error(errMsg, err);
            }
        };

        /**
         * flag to indicate if state has been initialized during current session
         * 
         * @private
         * @returns {boolean} Returns true or false to indicate initialization status
         */        
        isInitialized = function () {
            return !isUndefinedOrNull(state);
        };

        /**
         * 
         * Reset the state in local storage to the default state
         */
        resetState = function () {
            ontech.removeCookie(SESSION_KEY);
            init();
            ontech.log.info('resetState');
        }

        return {
            clear: clearState,
            comment: logActivity,
            defaultState: ontech.defaultState,            
            resetState: resetState,
            do: doAction,            
            get: getState,
            getStateJson: getStateJson,
            init: init,
            isInitialized,
            set: setState
        };
    } catch (err) {
        let errMsg = 'Error in ontech.state';
        ontech.log.error(errMsg, err);
    }
})(actions);

$(document).ready(function () {
    // init state if not yet initialized
    if (!ontech.state.isInitialized()) {
        ontech.state.init();
    }
});